import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

/**
 * PublicLegalLayout — Layout fuer die oeffentlichen, unauthentifizierten
 * Rechtstexte (/imprint, /privacy).
 *
 * F-PUBLIC-LEGAL-1: Impressum (TMG/DDG) und Datenschutzerklaerung (Art. 13
 * DSGVO) muessen ohne Login erreichbar sein. Dieses Layout wird NICHT unter
 * dem `_auth`-Guard gemountet, sondern von den Top-Level-Routen
 * `routes/imprint.tsx` und `routes/privacy.tsx` verwendet. Es rendert
 * dieselben Inhalts-Komponenten (ImprintPage/PrivacyPage) wie die
 * eingeloggte `/legal/*`-Ansicht (LegalLayout) — keine inhaltliche
 * Divergenz, nur ein anderer Navigations-Rahmen.
 */
export function PublicLegalLayout({
  children,
  activeTab,
}: {
  children: ReactNode
  activeTab: 'imprint' | 'privacy'
}) {
  const { t } = useTranslation('legal')

  const tabStyle = {
    padding: '8px 16px',
    textDecoration: 'none',
    color: 'var(--text)',
    borderBottom: '2px solid transparent',
  } as const

  const tabActiveStyle = {
    borderBottomColor: 'var(--c-primary, #6366f1)',
    color: 'var(--c-primary, #6366f1)',
    fontWeight: 600,
  } as const

  return (
    <div className="public-layout" data-testid="public-legal-layout">
      <header className="public-header">
        <div className="public-header__inner">
          <Link to="/" className="public-header__brand">
            <div className="logo-mark">H</div>
            <span className="public-header__name">HeliumTracker</span>
          </Link>
          <nav className="public-header__nav">
            <Link to="/" className="btn">
              {t('backToHome')}
            </Link>
          </nav>
        </div>
      </header>

      <main className="public-main">
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '32px 16px' }}>
          <div className="section-header">
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 8px 0' }}>{t('title')}</h1>
              <p className="muted" style={{ margin: 0 }}>
                {t('subtitle')}
              </p>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: 32,
              borderBottom: '1px solid var(--border, #e5e7eb)',
            }}
          >
            <Link
              to="/imprint"
              style={activeTab === 'imprint' ? { ...tabStyle, ...tabActiveStyle } : tabStyle}
            >
              {t('tabs.imprint')}
            </Link>
            <Link
              to="/privacy"
              style={activeTab === 'privacy' ? { ...tabStyle, ...tabActiveStyle } : tabStyle}
            >
              {t('tabs.privacy')}
            </Link>
          </div>

          <div className="card card-pad">{children}</div>
        </div>
      </main>
    </div>
  )
}
